// ============================================
// STAT CARD COMPONENT
// ============================================

import { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { cn, formatNumber } from '@/utils';

interface StatCardProps {
  title: string;
  value: number | string;
  icon?: ReactNode;
  iconColor?: string;
  change?: {
    value: number;
    label?: string;
  };
  className?: string;
}

export function StatCard({
  title,
  value,
  icon,
  iconColor = 'bg-maritime-100 text-maritime-600',
  change,
  className,
}: StatCardProps) {
  const isPositive = change && change.value >= 0;

  return (
    <div className={cn('bg-white rounded-maritime border border-gray-200 p-6', className)}>
      <div className="flex items-center gap-4">
        {icon && (
          <div className={cn('flex-shrink-0 p-3 rounded-maritime', iconColor)}>
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          <p className="text-2xl font-bold text-gray-900">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {change && (
            <div className="flex items-center gap-1 mt-1">
              {isPositive ? (
                <TrendingUp className="w-4 h-4 text-green-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500" />
              )}
              <span
                className={cn(
                  'text-xs font-medium',
                  isPositive ? 'text-green-600' : 'text-red-600'
                )}
              >
                {isPositive ? '+' : ''}
                {change.value}%
              </span>
              {change.label && (
                <span className="text-xs text-gray-400">{change.label}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
